/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import style from "../styles/shop.module.scss";
import Banner from "../components/shared/Banner";
import ProductCard from "../components/shared/ProductCard";
import ReactPaginate from "react-paginate";
import Loader from "../components/shared/Loader";
import { GlobalContext } from "../context/GlobalContext";
import withHelmet from "../seo/withHelmet";
import { shopHelmetProps } from "../seo/helmetProps";
import { debounce } from "lodash";

const Shop = () => {
  const {
    getProducts,
    products,
    loading,
    pagination,
    getCategories,
    categories,
  } = useContext(GlobalContext);

  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to find category ID by slug
  const findCategoryIdBySlug = useCallback(
    (categorySlug) => {
      if (!categorySlug || !categories.length) return null;
      for (const category of categories) {
        const found = category.subcategories.find(
          (sub) => sub.slug === categorySlug
        );
        if (found) return found.documentId;
      }
      return null;
    },
    [categories]
  );

  // Helper function to find category slug by ID
  const findCategorySlugById = useCallback(
    (categoryId) => {
      if (!categoryId || !categories.length) return null;
      for (const category of categories) {
        const found = category.subcategories.find(
          (sub) => sub.documentId === categoryId
        );
        if (found) return found.slug;
      }
      return null;
    },
    [categories]
  );

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const urlPage = searchParams.get("page");
    return urlPage ? parseInt(urlPage) : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubctegory, setSelectedSubctegory] = useState(() => {
    const urlCategory = searchParams.get("category");
    return urlCategory || null;
  });
  const [otherFilter, setOtherFilter] = useState(() => {
    return searchParams.get("filter") || null;
  });
  const [search, setSearch] = useState(() => {
    return searchParams.get("search") || "";
  });
  const [searchInput, setSearchInput] = useState(() => {
    return searchParams.get("search") || "";
  });
  const [hasConvertedFromUrl, setHasConvertedFromUrl] = useState(false);
  const isFirstRender = useRef(true);

  // Update URL params when filters change
  useEffect(() => {
    if (categories.length === 0) return;

    const params = new URLSearchParams();

    if (page > 1) params.set("page", page.toString());
    if (
      selectedSubctegory &&
      selectedSubctegory !== "" &&
      selectedSubctegory !== "Kategoria"
    ) {
      const categorySlug = findCategorySlugById(selectedSubctegory);
      if (categorySlug) {
        params.set("category", categorySlug);
      }
    }
    if (otherFilter) params.set("filter", otherFilter);
    if (search) params.set("search", search);

    setSearchParams(params, { replace: true });
  }, [
    page,
    selectedSubctegory,
    otherFilter,
    search,
    setSearchParams,
    findCategorySlugById,
    categories.length,
  ]);

  useEffect(() => {
    getCategories();
  }, []);

  // Convert URL category slug to ID when categories load
  useEffect(() => {
    if (categories.length > 0 && selectedSubctegory && !hasConvertedFromUrl) {
      const categoryId = findCategoryIdBySlug(selectedSubctegory);
      if (categoryId && categoryId !== selectedSubctegory) {
        setSelectedSubctegory(categoryId);
        setHasConvertedFromUrl(true);
      } else if (!categoryId && categories.length > 0) {
        const isValidId = categories.some((cat) =>
          cat.subcategories.some((sub) => sub.documentId === selectedSubctegory)
        );
        if (isValidId) {
          setHasConvertedFromUrl(true);
        } else {
          setSelectedSubctegory(null);
          setHasConvertedFromUrl(true);
        }
      }
    }
  }, [
    categories,
    findCategoryIdBySlug,
    selectedSubctegory,
    hasConvertedFromUrl,
  ]);

  useEffect(() => {
    if (pagination) {
      setTotalPages(pagination.pageCount);
    }
  }, [pagination]);

  const handleSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(1);
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    handleSearch(value);
  };
  useEffect(() => {
    // Skip on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Don't call API if we have a category slug but it hasn't been converted yet
    if (selectedSubctegory && !hasConvertedFromUrl && categories.length > 0) {
      return;
    }

    getProducts(
      page,
      8,
      selectedSubctegory === "Kategoria" || selectedSubctegory === "Të Gjitha"
        ? null
        : selectedSubctegory,
      otherFilter === "price:asc" ||
        otherFilter === "price:desc" ||
        otherFilter === "createdAt:desc"
        ? otherFilter
        : null,
      otherFilter === "discount" ? true : null,
      otherFilter === "top" ? true : null,
      search
    );
  }, [
    page,
    selectedSubctegory,
    otherFilter,
    search,
    hasConvertedFromUrl,
    categories.length,
  ]);

  return (
    <div className={style.wrapper}>
      <Banner
        title={"Shop"}
        current={"Shop"}
        from={[{ name: "Ballina", link: "/" }]}
      />

      <div className={style.filters}>
        <input
          type="text"
          placeholder="Kërko..."
          value={searchInput}
          onChange={handleSearchInputChange}
        />
        {categories.length === 0 || categories === undefined ? (
          <></>
        ) : (
          <div className={style.selects}>
            <select
              value={selectedSubctegory || ""}
              onChange={(e) => {
                setPage(1);
                setSelectedSubctegory(e.target.value || null);
              }}
            >
              <option value="Kategoria" disabled>
                Kategoria
              </option>
              <option value="">Të Gjitha</option>
              {categories.map((category) => (
                <React.Fragment key={category.id}>
                  <option disabled>{category.name}</option>

                  {category.subcategories.map((sub) => (
                    <option key={sub.id} value={sub.documentId}>
                      {sub.name}
                    </option>
                  ))}
                </React.Fragment>
              ))}
            </select>
            <select
              value={otherFilter || ""}
              onChange={(e) => {
                setPage(1);
                setOtherFilter(e.target.value || null);
              }}
            >
              <option value="">Sipas Relevancës</option>
              <option value={"price:asc"}>Çmimi më i ulët</option>
              <option value={"price:desc"}>Çmimi më i lartë</option>
              <option value={"discount"}>Me Zbritje</option>
              <option value={"createdAt:desc"}>Më të rejat</option>
              <option value={"top"}>Më të shiturat</option>
            </select>
          </div>
        )}
      </div>

      {loading || products === undefined || pagination === undefined ? (
        <Loader />
      ) : (
        <>
          {products.length === 0 ? (
            <h1>Nuk ka produkte</h1>
          ) : (
            <div className={style.products}>
              {products?.map((product) => {
                return <ProductCard product={product} key={product?.id} />;
              })}
            </div>
          )}
        </>
      )}
      <div className={style.paginationWrapper}>
        <ReactPaginate
          containerClassName={style.pagination}
          pageClassName={style.pageItem}
          activeClassName={style.active}
          onPageChange={(event) => {
            setPage(event.selected + 1);
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
          pageCount={totalPages}
          forcePage={page - 1}
          breakLabel="..."
          previousLabel={false}
          nextLabel={false}
        />
      </div>
    </div>
  );
};

export default withHelmet(Shop, shopHelmetProps);
